import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowRight, Lightbulb } from "lucide-react";

function normalize(s) {
  return (s || "").trim().toLowerCase().replace(/[.,!?;:]/g, "").replace(/ß/g, "ss").replace(/\s+/g, " ");
}

export default function Exercise({ activity, index, onResult, onNext, isLast }) {
  const [checked, setChecked] = useState(false);
  const [correct, setCorrect] = useState(false);
  const [selected, setSelected] = useState(null);
  const [text, setText] = useState("");
  const [ordered, setOrdered] = useState([]);
  const [pool, setPool] = useState(() => activity.type === "order_words" ? [...activity.words] : []);
  const [matches, setMatches] = useState({});

  const t = activity.type;

  const report = (isCorrect) => {
    setCorrect(isCorrect);
    setChecked(true);
    onResult && onResult({ correct: isCorrect, activity_type: t, activity_index: index, detail: activity.explanation || "" });
  };

  const check = () => {
    if (t === "multiple_choice" || t === "swiss_choice" || t === "listen_select") {
      report(selected === activity.answer);
    } else if (t === "fill_blank") {
      report(normalize(text) === normalize(activity.answer));
    } else if (t === "translate") {
      const acc = (activity.accepted_answers || []).map(normalize);
      report(acc.includes(normalize(text)));
    } else if (t === "write_short") {
      const acc = (activity.accepted_answers || []).map(normalize);
      report(acc.length === 0 ? text.trim().length > 2 : acc.includes(normalize(text)));
    } else if (t === "order_words") {
      report(ordered.join(" ") === activity.answer);
    } else if (t === "match") {
      const allRight = activity.pairs.every(([de, es]) => matches[de] === es);
      report(allRight && Object.keys(matches).length === activity.pairs.length);
    }
  };

  const canCheck = () => {
    if (t === "multiple_choice" || t === "swiss_choice" || t === "listen_select") return selected != null;
    if (t === "fill_blank" || t === "translate" || t === "write_short") return text.trim().length > 0;
    if (t === "order_words") return ordered.length === activity.words.length;
    if (t === "match") return Object.keys(matches).length === activity.pairs.length;
    return false;
  };

  const TYPE_LABELS = {
    multiple_choice: "Selección múltiple", swiss_choice: "Diferencia suiza", fill_blank: "Completa el espacio",
    match: "Relaciona", order_words: "Ordena las palabras", listen_select: "Escucha y selecciona",
    write_short: "Escribe tu respuesta", translate: "Traduce",
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6" data-testid={`exercise-${index}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-accent">{TYPE_LABELS[t] || t}</span>
        {activity.level && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">{activity.level}</span>}
      </div>
      <p className="font-display font-semibold text-lg mb-5">{activity.prompt}</p>

      {(t === "multiple_choice" || t === "swiss_choice" || t === "listen_select") && (
        <div className="grid gap-2.5">
          {t === "listen_select" && <p className="text-sm text-muted-foreground mb-1">Lee la palabra del enunciado y elige la opción correcta.</p>}
          {activity.options.map((opt) => {
            const isSel = selected === opt;
            const showRight = checked && opt === activity.answer;
            const showWrong = checked && isSel && opt !== activity.answer;
            return (
              <button key={opt} disabled={checked} onClick={() => setSelected(opt)}
                data-testid={`option-${index}-${opt}`}
                className={`text-left px-4 py-3 rounded-md border transition-colors text-sm font-medium
                  ${showRight ? "border-emerald-500 bg-emerald-500/10" : showWrong ? "border-destructive bg-destructive/10" :
                    isSel ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary"}`}>
                <span className="flex items-center justify-between">
                  {opt}
                  {showRight && <Check size={16} className="text-emerald-600" />}
                  {showWrong && <X size={16} className="text-destructive" />}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {(t === "fill_blank" || t === "translate" || t === "write_short") && (
        <input value={text} onChange={(e) => setText(e.target.value)} disabled={checked}
          data-testid={`input-${index}`} placeholder="Escribe aquí tu respuesta..."
          className={`w-full h-12 rounded-md border px-4 text-sm ${checked ? (correct ? "border-emerald-500 bg-emerald-500/10" : "border-destructive bg-destructive/10") : "border-input bg-background"}`} />
      )}

      {t === "order_words" && (
        <div>
          <div className="min-h-[52px] flex flex-wrap gap-2 rounded-md border border-dashed border-border p-3 mb-3" data-testid={`order-drop-${index}`}>
            {ordered.map((w, i) => (
              <button key={i} disabled={checked} onClick={() => { setOrdered(ordered.filter((_, j) => j !== i)); setPool([...pool, w]); }}
                className="px-3 py-1.5 rounded-md bg-primary/10 border border-primary/30 text-sm font-medium">{w}</button>
            ))}
            {ordered.length === 0 && <span className="text-sm text-muted-foreground self-center">Toca las palabras en orden</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            {pool.map((w, i) => (
              <button key={i} disabled={checked} onClick={() => { setOrdered([...ordered, w]); setPool(pool.filter((_, j) => j !== i)); }}
                data-testid={`word-${index}-${w}`}
                className="px-3 py-1.5 rounded-md border border-border bg-secondary text-sm font-medium hover:border-primary/50 transition-colors">{w}</button>
            ))}
          </div>
        </div>
      )}

      {t === "match" && (
        <div className="grid gap-2.5">
          {activity.pairs.map(([de, es]) => (
            <div key={de} className="flex items-center gap-3">
              <span className="w-1/2 text-sm font-semibold">{de}</span>
              <select disabled={checked} value={matches[de] || ""} onChange={(e) => setMatches({ ...matches, [de]: e.target.value })}
                data-testid={`match-${index}-${de}`}
                className={`w-1/2 h-10 rounded-md border px-3 text-sm ${checked ? (matches[de] === es ? "border-emerald-500" : "border-destructive") : "border-input bg-background"}`}>
                <option value="">Elige...</option>
                {activity.pairs.map(([, opt]) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}

      {checked && (
        <div className={`mt-5 rounded-md p-4 ${correct ? "bg-emerald-500/10" : "bg-amber-500/10"}`} data-testid={`feedback-${index}`}>
          <div className="flex items-center gap-2 font-semibold text-sm mb-1">
            {correct ? <><Check size={16} className="text-emerald-600" /> ¡Correcto!</> : <><Lightbulb size={16} className="text-amber-600" /> Repasemos</>}
          </div>
          {!correct && activity.answer && <p className="text-sm mb-1">Respuesta: <strong>{activity.answer}</strong></p>}
          {!correct && activity.accepted_answers?.length > 0 && <p className="text-sm mb-1">Ejemplo válido: <strong>{activity.accepted_answers[0]}</strong></p>}
          <p className="text-sm text-muted-foreground">{activity.explanation}</p>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        {!checked ? (
          <Button onClick={check} disabled={!canCheck()} data-testid={`check-${index}`}>Comprobar</Button>
        ) : (
          <Button onClick={onNext} data-testid={`next-${index}`} className="rounded-full">
            {isLast ? "Finalizar" : "Siguiente"} <ArrowRight size={16} className="ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
